import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Spinner,
  Table,
  Tag,
  Text,
} from '@nimbus-ds/components';
import { LinkOffIcon } from '@nimbus-ds/icons';
import { PartnersListItemResponseV2Dto } from '@tiendanube/common';
import { Stack } from 'commons/components';
import { useStoreStatus } from 'domains/Auth/hooks';
import { useUpdatePartnerAdminAccessMutation } from 'domains/Configurations/Users/usersApi';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import ShowMoreRoles from '../../../UsersListDesktop/components/ShowMoreRoles';

interface PartnersListDesktopRowProps {
  partner: PartnersListItemResponseV2Dto;
  storeRoles: Record<string, string>;
  hasPartnerAdminAccess?: boolean;
  onUnlinkPartner: (partner: PartnersListItemResponseV2Dto) => void;
}

const PAGE_SIZE = 10;
const VIEWABLE_TAG_AMOUNT = 7;

function PartnersListDesktopRow({
  partner,
  storeRoles,
  hasPartnerAdminAccess,
  onUnlinkPartner,
}: Readonly<PartnersListDesktopRowProps>) {
  const t = useTranslationConfigurations('usersListPage.table');
  const [shownRoles, setShownRoles] = useState(VIEWABLE_TAG_AMOUNT);

  const [updatePartnerAdminAccess, { isLoading: loadingUpdateAdminAccess }] =
    useUpdatePartnerAdminAccessMutation();

  const showMoreRoles = () => setShownRoles((prev) => prev + PAGE_SIZE);
  const showLessRoles = () => setShownRoles((prev) => prev - PAGE_SIZE);

  const { storeStatus } = useStoreStatus();

  const filteredPermissions = useMemo(
    () =>
      partner?.roles?.filter?.((permission) => {
        const label = storeRoles?.[permission];
        return typeof label === 'string' && label?.trim()?.length > 0;
      }),
    [partner?.roles, storeRoles],
  );

  const showMoreRolesLink =
    filteredPermissions && filteredPermissions.length > VIEWABLE_TAG_AMOUNT;

  const hasMoreRoles =
    filteredPermissions && filteredPermissions.length > shownRoles;

  const handleAcceptOrRefuse = (
    permissionId: number,
    partnerId: number,
    status: boolean,
  ) => {
    updatePartnerAdminAccess({
      payload: {
        data: { permissionId, partnerId, status },
      },
    });
  };

  return (
    <Table.Row>
      <Table.Cell>
        <Stack spacing="tight">
          <Box display="flex" flexDirection="column" gap="1">
            <Text lineClamp={1}>
              {partner?.partnerName || t('withoutName')}
            </Text>
          </Box>
        </Stack>
      </Table.Cell>
      <Table.Cell>
        <Text lineClamp={1}>{partner?.partnerEmail || t('withoutName')}</Text>
      </Table.Cell>
      <Table.Cell>
        <Stack column align="flex-start" spacing="tight">
          <Stack spacing="tight" wrap>
            {filteredPermissions?.slice?.(0, shownRoles)?.map?.((roles) => (
              <Tag key={`partner-${partner?.partnerId}-${roles}`}>
                <Text fontSize="caption" lineClamp={1}>
                  {storeRoles?.[roles]}
                </Text>
              </Tag>
            ))}
          </Stack>
          {showMoreRolesLink && (
            <ShowMoreRoles
              hasMoreRoles={hasMoreRoles}
              showMoreRoles={showMoreRoles}
              showLessRoles={showLessRoles}
            />
          )}
        </Stack>
      </Table.Cell>
      <Table.Cell>
        {hasPartnerAdminAccess && partner.status === 'pending' ? (
          <Box display="flex" gap="2" alignItems="center" flexDirection="row">
            <Button
              appearance="neutral"
              onClick={() =>
                handleAcceptOrRefuse(partner?.id, partner?.partnerId, false)
              }
              disabled={loadingUpdateAdminAccess}
            >
              {t('refuse')}
              {loadingUpdateAdminAccess && (
                <Spinner color="currentColor" size="small" />
              )}
            </Button>
            <Button
              appearance="primary"
              onClick={() =>
                handleAcceptOrRefuse(partner?.id, partner?.partnerId, true)
              }
              disabled={loadingUpdateAdminAccess}
            >
              {t('accept')}
              {loadingUpdateAdminAccess && (
                <Spinner color="currentColor" size="small" />
              )}
            </Button>
          </Box>
        ) : partner?.status === 'granted' ? (
          <IconButton
            size="2rem"
            source={<LinkOffIcon />}
            onClick={() => onUnlinkPartner(partner)}
            disabled={storeStatus === 'development'}
          />
        ) : (
          <Box>-</Box>
        )}
      </Table.Cell>
    </Table.Row>
  );
}

export default PartnersListDesktopRow;
